import { c as _c } from "react/compiler-runtime";
import type { EChartsType } from 'echarts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { findKeyAtBackPosition, type KeyboardPlacement } from '@/lib/piano/keyboard/keyboard-geometry';
export interface HoveredKeyApi {
  /** Pass to `<ReactECharts onChartReady>`. */
  bindChart: (chart: EChartsType) => void;
  /** Spread on the figure: makes it focusable and the arrows move the reading. */
  figureProps: {
    onKeyDown: React.KeyboardEventHandler;
    tabIndex: 0;
  };
  hoveredKey: number | null;
  setHoveredKey: (keyNumber: number | null) => void;
}

/**
 * Which key the reader is looking at.
 *
 * One piece of state feeds the brass band across the plot, the key lit on the
 * keyboard, the octave mark in bold and the reading panel beside the chart.
 * It comes from the pointer over the plot, from the pointer over the keyboard,
 * or from the arrow keys.
 */
export const useHoveredKey = (geometry, keyCount, t0) => {
  const $ = _c(13);
  const gridCount = t0 === undefined ? 1 : t0;
  const [hoveredKey, setHoveredKey] = useState(null);
  const geometryRef = useRef(geometry);
  let t1;
  let t2;
  if ($[0] !== geometry) {
    t1 = () => {
      geometryRef.current = geometry;
    };
    t2 = [geometry];
    $[0] = geometry;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== gridCount) {
    t3 = chart => {
      const zr = chart.getZr();
      const onMove = event => {
        const point = [event.offsetX, event.offsetY];
        const gridIndex = Array.from({
          length: gridCount
        }, _temp).find(index => chart.containPixel({
          gridIndex: index
        }, point));
        if (gridIndex === undefined) {
          setHoveredKey(null);
          return;
        }
        const [position] = [chart.convertFromPixel({
          gridIndex
        }, point)].flat();
        setHoveredKey(position === undefined ? null : findKeyAtBackPosition(geometryRef.current, position));
      };
      const onOut = () => setHoveredKey(null);
      zr.on("mousemove", onMove);
      zr.on("globalout", onOut);
    };
    $[3] = gridCount;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const bindChart = t3;
  let t4;
  if ($[5] !== keyCount) {
    t4 = event_0 => {
      if (event_0.key !== "ArrowLeft" && event_0.key !== "ArrowRight") {
        return;
      }
      event_0.preventDefault();
      setHoveredKey(current => {
        if (current === null) {
          return Math.ceil(keyCount / 2);
        }
        const next = current + (event_0.key === "ArrowRight" ? 1 : -1);
        return Math.min(Math.max(next, 1), keyCount);
      });
    };
    $[5] = keyCount;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onKeyDown = t4;
  let t5;
  if ($[7] !== onKeyDown) {
    t5 = {
      onKeyDown,
      tabIndex: 0
    };
    $[7] = onKeyDown;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== bindChart || $[10] !== hoveredKey || $[11] !== t5) {
    t6 = {
      bindChart,
      figureProps: t5,
      hoveredKey,
      setHoveredKey
    };
    $[9] = bindChart;
    $[10] = hoveredKey;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
function _temp(_, i) {
  return i;
}